import { useRef, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  LockKeyhole,
  UserPlus,
} from "lucide-react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { api, ApiError, jsonBody } from "./api";
import type { Usuario } from "./types";
import { Button, Field, Input, InlineNotice } from "./components";

gsap.registerPlugin(useGSAP);

export default function Login({
  onSuccess,
}: {
  onSuccess: (user: Usuario) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("kelvin@automyx.com.br");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [name, setName] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const mutation = useMutation({
    mutationFn: () =>
      api<{ usuario: Usuario }>("/autenticacao/entrar", {
        method: "POST",
        ...jsonBody({
          email,
          senha: password,
          codigo_mfa: mfaCode || undefined,
        }),
      }),
    onSuccess: (data) => onSuccess(data.usuario),
    onError: (error) => {
      if (error instanceof ApiError && error.mfaNecessario)
        setMfaRequired(true);
    },
  });
  const recovery = useMutation({
    mutationFn: () =>
      api<{ mensagem: string }>("/autenticacao/recuperar", {
        method: "POST",
        ...jsonBody({ email }),
      }),
  });
  const registration = useMutation({
    mutationFn: () =>
      api<{ mensagem: string }>("/autenticacao/cadastrar", {
        method: "POST",
        ...jsonBody({
          nome: name,
          email,
          senha: password,
          confirmar_senha: passwordConfirmation,
        }),
      }),
  });

  useGSAP(
    () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const timeline = gsap.timeline({ defaults: { ease: "power3.out" } });
      timeline
        .from(".login__photograph", { scale: 1.025, duration: 0.9 })
        .from(".login__institution > *", { y: 10, duration: 0.32, stagger: 0.06 }, "-=0.68")
        .from(
          ".login__form > *",
          { y: 10, duration: 0.3, stagger: 0.055 },
          "-=0.5",
        );
    },
    { scope: rootRef },
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    if (creatingAccount) registration.mutate();
    else mutation.mutate();
  }

  const activeMutation = creatingAccount ? registration : mutation;
  const error = activeMutation.error instanceof ApiError ? activeMutation.error : null;
  return (
    <main className="login" ref={rootRef}>
      <section className="login__visual" aria-label="Contexto da Santa Casa">
        <img
          className="login__photograph"
          src="/santa-casa-turno.webp"
          alt="Mesa de passagem de turno com escala de equipe e materiais de registro organizados"
        />
        <div className="login__institution">
          <p>Santa Casa · Central de hemodiálise</p>
          <h1>Registro de turno</h1>
          <span>Pacientes, sessões, insumos e produção no mesmo histórico operacional.</span>
        </div>
      </section>
      <section className="login__access">
        <form className="login__form" onSubmit={submit} noValidate>
          <div className="login__form-title">
            <Building2 size={22} />
            <div>
              <p>Santa Casa</p>
              <h2>{creatingAccount ? "Solicitar acesso" : "Entrada da equipe"}</h2>
            </div>
          </div>
          {registration.data ? (
            <InlineNotice tone="success" title="Solicitação enviada">
              {registration.data.mensagem}
            </InlineNotice>
          ) : null}
          {error ? (
            <InlineNotice tone="danger" title={error.message}>
              {error.recuperacao || "Revise os dados e tente novamente."}
            </InlineNotice>
          ) : null}
          {creatingAccount ? (
            <Field label="Nome completo" required error={error?.campos?.nome}>
              <Input
                name="nome"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                minLength={3}
                required
              />
            </Field>
          ) : null}
          <Field label="E-mail" required error={error?.campos?.email}>
            <Input
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              inputMode="email"
              required
            />
          </Field>
          <Field label="Senha" required error={error?.campos?.senha}>
            <div className="password-input">
              <Input
                type={showPassword ? "text" : "password"}
                name="senha"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={creatingAccount ? "new-password" : "current-password"}
                minLength={creatingAccount ? 10 : undefined}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Field>
          {creatingAccount ? (
            <Field
              label="Confirmar senha"
              required
              error={error?.campos?.confirmar_senha}
              hint="Use 10 caracteres ou mais, com maiúscula, minúscula e número."
            >
              <Input
                type={showPassword ? "text" : "password"}
                name="confirmar_senha"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </Field>
          ) : null}
          {mfaRequired ? (
            <Field
              label="Código de verificação"
              required
              hint="Seis dígitos do autenticador institucional."
            >
              <Input
                name="codigo_mfa"
                value={mfaCode}
                onChange={(event) =>
                  setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                minLength={6}
                maxLength={6}
                required
              />
            </Field>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={
              activeMutation.isPending ||
              !email ||
              !password ||
              (creatingAccount && (!name.trim() || !passwordConfirmation || Boolean(registration.data))) ||
              (!creatingAccount && mfaRequired && mfaCode.length !== 6)
            }
          >
            {activeMutation.isPending ? (
              <>
                <LockKeyhole className="spin" size={17} />
                {creatingAccount ? "Enviando solicitação" : "Validando acesso"}
              </>
            ) : creatingAccount ? (
              <>
                Enviar para aprovação
                <UserPlus size={17} />
              </>
            ) : (
              <>
                Entrar na central
                <ArrowRight size={17} />
              </>
            )}
          </Button>
          <div className="login__secondary-actions">
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setCreatingAccount((value) => !value);
                setRecoveryOpen(false);
                registration.reset();
                mutation.reset();
                setMfaRequired(false);
              }}
            >
              {creatingAccount ? "Voltar para entrar" : "Criar sua conta"}
            </button>
            {!creatingAccount ? (
              <button
                className="text-button"
                type="button"
                onClick={() => {
                  setRecoveryOpen((value) => !value);
                  recovery.reset();
                }}
              >
                Recuperar acesso
              </button>
            ) : null}
          </div>
          {recoveryOpen ? (
            <div className="login__recovery">
              <p>
                Registre a solicitação para validação segura pela administração
                da unidade.
              </p>
              {recovery.data ? (
                <InlineNotice tone="success" title="Solicitação registrada">
                  {recovery.data.mensagem}
                </InlineNotice>
              ) : (
                <Button
                  type="button"
                  onClick={() => recovery.mutate()}
                  disabled={!email || recovery.isPending}
                >
                  {recovery.isPending
                    ? "Registrando…"
                    : "Registrar solicitação"}
                </Button>
              )}
              {recovery.error instanceof ApiError ? (
                <InlineNotice tone="danger" title={recovery.error.message}>
                  {recovery.error.recuperacao || "Revise o e-mail informado."}
                </InlineNotice>
              ) : null}
            </div>
          ) : null}
          <p className="login__security">
            Sessão protegida · ações sensíveis auditadas
          </p>
        </form>
      </section>
    </main>
  );
}
